import { ChangeDetectionStrategy, Component, Optional, Self, input, output, signal } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { SharedMaterialUIModule } from '../../modules/shared-material-ui.module';

let nextId = 0;

// A Yes / No question that can also be left unanswered: the control holds true, false or null, and a "Clear answer" button takes an answer
// back to null. Use it as `<yes-no-toggle formControlName="x" label="..." />`. A disabled control (a read-only form) shows the answer
// without the clear button.
@Component({
  selector: 'yes-no-toggle',
  imports: [...SharedMaterialUIModule],
  templateUrl: './yes-no-toggle.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class YesNoToggleComponent implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly clearable = input(true);

  // The answer was taken back, for a caller that has more to undo (a reason given for a No, for example).
  readonly cleared = output<void>();

  protected readonly labelId = `yes-no-toggle-${nextId++}`;
  // Signals, because the form can write a value at any time and the view must follow without an event.
  protected readonly value = signal<boolean | null>(null);
  protected readonly disabled = signal(false);

  private onValueChanged: (value: boolean | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(@Optional() @Self() ngControl: NgControl | null) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  writeValue(value: boolean | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: boolean | null) => void): void {
    this.onValueChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected answer(value: boolean): void {
    this.value.set(value);
    this.onValueChanged(value);
    this.onTouched();
  }

  protected clear(): void {
    this.value.set(null);
    this.onValueChanged(null);
    this.onTouched();
    this.cleared.emit();
  }
}
